import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, timeout: 25000 });

// Persistent per-browser device id (one physical/browser install == one device).
// Survives refresh, logout/login and SW updates; only cleared if the user wipes storage.
export function getDeviceId() {
  let id = localStorage.getItem("psc_device_id");
  if (!id) {
    id = (window.crypto && crypto.randomUUID) ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem("psc_device_id", id);
  }
  return id;
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("psc_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers["X-Device-Id"] = getDeviceId();
  // Active module — lets the backend enforce strict per-module content isolation
  // (can't be bypassed by omitting a ?module= query param).
  try {
    const m = localStorage.getItem("ch_active_module");
    if (m) config.headers["X-Active-Module"] = m;
  } catch { /* localStorage unavailable — backend falls back to default module */ }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    try {
      const { recordApiError } = require("@/lib/guardianLog");
      recordApiError(err);
    } catch { /* guardian log is best-effort */ }
    const detail = err?.response?.data?.detail;
    // Read-Only Access mode — central subscription gate returns 402 for writes.
    if (err?.response?.status === 402 && detail && typeof detail === "object" && detail.code === "READ_ONLY") {
      try {
        const { toast } = require("sonner");
        toast.error(detail.message, { id: "read-only-mode" });
      } catch { /* toast best-effort */ }
      err.response.data.detail = detail.message;
      return Promise.reject(err);
    }
    // Normalize FastAPI/Pydantic 422 "detail" (array of error objects) into a
    // readable string — passing raw objects to toast/JSX crashes React.
    // Structured object payloads (e.g. signup duplicate lists) are left intact.
    const isValidationShape = Array.isArray(detail) || (detail && typeof detail === "object" && ("msg" in detail || "loc" in detail));
    if (detail && typeof detail !== "string" && isValidationShape) {
      const msgs = (Array.isArray(detail) ? detail : [detail]).map((d) => {
        if (typeof d === "string") return d;
        const field = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== "body").join(".") : "";
        return field ? `${field}: ${d?.msg || "invalid value"}` : (d?.msg || "Invalid request");
      });
      err.response.data.detail = msgs.join(" · ");
    }
    if (err?.response?.status === 403 && detail && typeof detail === "object" && detail.code === "MUST_CHANGE_PASSWORD") {
      if (!window.location.pathname.startsWith("/set-password")) {
        setTimeout(() => { window.location.href = "/set-password"; }, 30);
      }
      err.response.data.detail = detail.message;
      return Promise.reject(err);
    }
    if (err?.response?.status === 403 && detail && typeof detail === "object" && detail.code === "ACCOUNT_DISABLED") {
      try { const { toast } = require("sonner"); toast.error(detail.message, { id: "account-disabled" }); } catch { /* best-effort */ }
      localStorage.removeItem("psc_token");
      localStorage.removeItem("psc_user");
      if (!window.location.pathname.startsWith("/login")) {
        setTimeout(() => { window.location.href = "/login"; }, 60);
      }
      err.response.data.detail = detail.message;
      return Promise.reject(err);
    }
    if (err?.response?.status === 401) {
      // If an admin support session dies (revoked/expired), fall back to the
      // stored admin identity instead of logging out.
      if (localStorage.getItem("psc_impersonation")) {
        const adminToken = localStorage.getItem("psc_admin_token");
        const adminUser = localStorage.getItem("psc_admin_user");
        localStorage.removeItem("psc_impersonation");
        localStorage.removeItem("psc_admin_token");
        localStorage.removeItem("psc_admin_user");
        if (adminToken) {
          localStorage.setItem("psc_token", adminToken);
          if (adminUser) localStorage.setItem("psc_user", adminUser);
          window.location.href = "/admin/accounts";
          return Promise.reject(err);
        }
      }
      const onAuthPage = window.location.pathname.startsWith("/login") || window.location.pathname.startsWith("/register");
      if (!onAuthPage) {
        // Preserve returnTo (with search + hash) so we can jump the user
        // right back to their inspection after re-login. The InspectionDetail
        // page will also stash the current form draft via its own catch()
        // handler *before* the redirect fires, because we schedule the nav
        // on the next tick.
        const returnTo = window.location.pathname + window.location.search + window.location.hash;
        localStorage.removeItem("psc_token");
        localStorage.removeItem("psc_user");
        setTimeout(() => {
          window.location.href = `/login?returnTo=${encodeURIComponent(returnTo)}`;
        }, 50);
      }
    }
    return Promise.reject(err);
  }
);

export default api;
